export type DashboardDataSource = "live" | "mock";

export type EnrollmentClinicOption = {
  id: string;
  name: string;
};

export type EnrollmentSnapshotMetrics = {
  total: number;
  enrolled: number;
  assessed: number;
  submitted: number;
  aligned: number;
  pending: number;
  newCount: number;
  unalignedCount: number;
  marketablePatients: number;
  pendingReferral: number;
  pendingSecondCall: number;
  inProgress: number;
  screened: number;
  reviewCount: number;
  archived: number;
  archivedAfterAligned: number;
  dailyArchivedVoluntary: number;
  dailyArchivedNonVoluntary: number;
  dailyArchived: number;
  archiveBeforeAlignedCount: number;
  mtdUnalignedCount: number;
  mtdUnengagedCount: number;
  monthlyE1: number;
  monthlyPendingReview: number;
  monthlyPendingReferrals: number;
  monthlyTotal: number;
  monthlyTarget: number;
  monthlyDelta: number;
};

export type EnrollmentSnapshotResponse = {
  date: string;
  data: EnrollmentSnapshotMetrics | null;
  generatedAt: string;
  source: DashboardDataSource;
};

export type EnrollmentSnapshotsResponse = {
  snapshots: Array<{ date: string; data: EnrollmentSnapshotMetrics | null; error?: string }>;
  generatedAt: string;
  source: DashboardDataSource;
};

export type EnrollmentCallMetrics = {
  totalBookings: number;
  cancelledCalls: number;
  rescheduledCalls: number;
  remainingCalls: number;
  nonCancelledCalls: number;
  noShowCalls: number;
  notEligibleCalls: number;
  /** @deprecated Use rescheduledCalls; this legacy field has no timing check. */
  lastMinuteRescheduleCalls: number;
  convertibleCalls: number;
  enrolledCalls: number;
  pendingReferralCalls: number;
  pendingSecondCallCalls: number;
};

export type EnrollmentEodCalls = {
  current: EnrollmentCallMetrics;
  previous: EnrollmentCallMetrics;
  last7Days: Array<EnrollmentCallMetrics & { date: string }>;
  scheduledRows: Array<{
    aiScheduled: number;
    date: string;
    esScheduled: number;
    webScheduled: number;
    unclassifiedScheduled: number;
    totalActive: number;
  }>;
};

export type EnrollmentConversionDetail = {
  carespaceName: string;
  hasEnrollmentEncounter: boolean;
  hasEnrollmentObjectionReasons: boolean;
  bookingStartAt: string;
  calComLink: string;
  calUid: string;
  cancelReason: string;
  cancelledBy: string;
  carespaceId: string;
  carespaceLink: string;
  clinicName: string;
  connectedToHuman: boolean;
  enrollmentStatus: string;
  esName: string;
  guideStatus: string;
  isCancelled: boolean;
  isRescheduled: boolean;
  isConvertible: boolean;
  isEnrolled: boolean;
  /** @deprecated Use isRescheduled; this legacy flag has no timing check. */
  isLastMinuteReschedule: boolean;
  isNoShow: boolean;
  isNotEligible: boolean;
  isPendingReferral: boolean;
  isPendingSecondCall: boolean;
  matchingCalls: Array<Record<string, unknown>>;
  notEligibleReason: string;
  rescheduledCalLink: string;
  rescheduledCalUid: string;
};

export type EnrollmentCumulativeRow = {
  alignedCount: number;
  date: string;
  matchedAlignedCount: number;
  matchedPendingReferralCount: number;
  matchedPendingSecondCallCount: number;
  matchedPendingWithCmsCount: number;
  pendingReferralCount: number;
  pendingSecondCallCount: number;
  pendingWithCmsCount: number;
};

export type EnrollmentReferralRow = {
  aligned15To30Days: number;
  aligned1To14Days: number;
  aligned30To60Days: number;
  aligned60To90Days: number;
  aligned90PlusDays: number;
  clinicName: string;
  initialCohortAligned: number;
  launchReferrals: number;
  month: string;
  referralRefreshAligned: number;
  refreshReferrals: number;
};

export type EnrollmentEodData = {
  calls: EnrollmentEodCalls;
  clinics: string[];
  conversionRows: EnrollmentConversionDetail[];
  cumulativeRows: EnrollmentCumulativeRow[];
  referrals: EnrollmentReferralRow[];
};

export type EnrollmentEodResponse = {
  data: EnrollmentEodData;
  date: string;
  generatedAt: string;
  source: DashboardDataSource;
};

export type EnrollmentEodCsvRow = {
  carespaceId: string;
  category: string;
  dateCounted: string;
};

export type EnrollmentEodCsvResponse = {
  date: string;
  generatedAt: string;
  rows: EnrollmentEodCsvRow[];
  source: DashboardDataSource;
};

export type EnrollmentReferralsResponse = {
  clinics: string[];
  date: string;
  generatedAt: string;
  referrals: EnrollmentReferralRow[];
  source: DashboardDataSource;
};

export type EnrollmentBookingSourceRow = {
  bookingCount: number;
  clinicId: string;
  clinicName: string;
  isCancelled: boolean;
  schedulingSource: string;
  weekStart: string;
};

export type EnrollmentBookingSourcesResponse = {
  clinics: EnrollmentClinicOption[];
  date: string;
  generatedAt: string;
  rows: EnrollmentBookingSourceRow[];
  source: DashboardDataSource;
};

export type EnrollmentNeuroAppointmentRow = {
  appointmentCount: number;
  carespaceCount: number;
  clinicId: string;
  clinicName: string;
  date: string;
  hasRetellCall: boolean;
};

export type EnrollmentNeuroAppointmentsResponse = {
  clinics: EnrollmentClinicOption[];
  date: string;
  generatedAt: string;
  rows: EnrollmentNeuroAppointmentRow[];
  source: DashboardDataSource;
};

export type EnrollmentEsOpsFailureRow = {
  attendeePhone: string;
  bookingStartEt: string;
  calComLink: string;
  calUid: string;
  esName: string;
  eventTitle: string;
};

export type EnrollmentEsOpsResponse = {
  date: string;
  generatedAt: string;
  rows: EnrollmentEsOpsFailureRow[];
  source: DashboardDataSource;
};

export type EnrollmentEsOpsCallFailureRow = {
  bookingStartEt: string;
  calComLink: string;
  calUid: string;
  carespaceId: string;
  carespaceLink: string;
  esName: string;
  eventTitle: string;
  flags: string[];
};

export type EnrollmentEsOpsCallsResponse = {
  date: string;
  generatedAt: string;
  rows: EnrollmentEsOpsCallFailureRow[];
  source: DashboardDataSource;
};

export type EnrollmentEsConversionRow = {
  conversionRate: number | null;
  eligibleStartedCalls: number;
  enrolled: number;
  esName: string;
  weekEnd: string;
  weekStart: string;
};

export type EnrollmentEsConversionResponse = {
  date: string;
  generatedAt: string;
  rows: EnrollmentEsConversionRow[];
  source: DashboardDataSource;
};

export type EnrollmentTrendRow = {
  cancelled: number;
  conversionRate: number;
  e1: number;
  e2: number;
  e3: number;
  enrolled: number;
  failedToConvert: number;
  noShow: number;
  noShowRate: number;
  notEligible: number;
  notEligibleRate: number;
  periodStart: string;
  scheduled: number;
  scheduledAi: number;
  scheduledEs: number;
  scheduledPractice: number;
};

export type EnrollmentTrendResponse = {
  end: string;
  generatedAt: string;
  rows: EnrollmentTrendRow[];
  source: DashboardDataSource;
  start: string;
  window: "week" | "month";
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasSource(value: Record<string, unknown>): boolean {
  return value.source === "live" || value.source === "mock";
}

export function isEnrollmentSnapshotResponse(value: unknown): value is EnrollmentSnapshotResponse {
  return isRecord(value) && typeof value.date === "string" && hasSource(value);
}

export function isEnrollmentSnapshotsResponse(
  value: unknown,
): value is EnrollmentSnapshotsResponse {
  return isRecord(value) && Array.isArray(value.snapshots) && hasSource(value);
}

export function isEnrollmentEodResponse(value: unknown): value is EnrollmentEodResponse {
  return (
    isRecord(value) && typeof value.date === "string" && isRecord(value.data) && hasSource(value)
  );
}

export function isEnrollmentEodCsvResponse(value: unknown): value is EnrollmentEodCsvResponse {
  return isRecord(value) && Array.isArray(value.rows) && hasSource(value);
}

export function isEnrollmentReferralsResponse(
  value: unknown,
): value is EnrollmentReferralsResponse {
  return (
    isRecord(value) &&
    Array.isArray(value.referrals) &&
    Array.isArray(value.clinics) &&
    hasSource(value)
  );
}

export function isEnrollmentBookingSourcesResponse(
  value: unknown,
): value is EnrollmentBookingSourcesResponse {
  return (
    isRecord(value) && Array.isArray(value.rows) && Array.isArray(value.clinics) && hasSource(value)
  );
}

export function isEnrollmentNeuroAppointmentsResponse(
  value: unknown,
): value is EnrollmentNeuroAppointmentsResponse {
  return (
    isRecord(value) && Array.isArray(value.rows) && Array.isArray(value.clinics) && hasSource(value)
  );
}

export function isEnrollmentEsOpsResponse(value: unknown): value is EnrollmentEsOpsResponse {
  return isRecord(value) && Array.isArray(value.rows) && hasSource(value);
}

export function isEnrollmentEsOpsCallsResponse(
  value: unknown,
): value is EnrollmentEsOpsCallsResponse {
  return isRecord(value) && Array.isArray(value.rows) && hasSource(value);
}

export function isEnrollmentEsConversionResponse(
  value: unknown,
): value is EnrollmentEsConversionResponse {
  return isRecord(value) && Array.isArray(value.rows) && hasSource(value);
}

export function isEnrollmentTrendResponse(value: unknown): value is EnrollmentTrendResponse {
  return isRecord(value) && Array.isArray(value.rows) && hasSource(value);
}
